// Design-faithful single-perspective condition editor (design:
// perspective-filter.jsx — PFAssigneeEditor). One row = ONE perspective
// condition (`PerspectiveFilter`). The perspective is chosen up top (glyph +
// dropdown), then two always-visible segmented controls express the question:
//   • "Match spheres that" Have it / Don't        → operator 'in' / 'not_in'
//   • "Assigned to"        Anyone / Specific      → whether an assignee narrowing applies
// When Specific, the assignee checklist (reused `PerspectiveSearch` — people /
// roles / teams with avatars) narrows to specific assignees. When Have it,
// an "At least N matching assignment(s)" counter sets `minCount`.
//
// Reuse: this molecule owns presentation only; assignee lookup is delegated to
// `PerspectiveSearch` (`molecules/Atlas/Filtering/PerspectiveSearch`) — no
// `PerspectiveFilterMolecule` refactor. Anyone/Specific is UI-only state (the
// canonical `PerspectiveFilter` has no `mode`; empty `assignees` == Anyone), so
// nothing new persists. See spec lane-in-group-editor.md Phase 4a (SC4).
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { PerspectiveFilter } from 'types/filters';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import { InputNumberAtom } from '../../atoms/InputNumber/InputNumberAtom';
import PerspectiveSearch, {
  type OptionType,
  type PerspectiveFilter as PerspectiveSearchFilter,
} from '../Atlas/Filtering/PerspectiveSearch';
import { useSphereInfo } from '../../../hooks/useSphereInfo';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import './PerspectiveValueEditorMolecule.scss';

export interface PerspectiveValueEditorMoleculeProps {
  /** Available perspectives for this filter context (schema-derived + inherited defaults). */
  availablePerspectives: PerspectiveOption[];
  /** The single `PerspectiveFilter` this editor instance edits (one row = one perspective). */
  value: PerspectiveFilter;
  onChange: (next: PerspectiveFilter) => void;
  /** Omit to hide the remove affordance — the host (e.g. FilterEditorPanel) may provide its own. */
  onRemove?: () => void;
  readOnly?: boolean;
  /** DOM index for `data-testid` parity with list hosts (e.g. `PerspectiveFilterMolecule`). Default 0. */
  index?: number;
  /**
   * Hides this editor's own perspective picker (the glyph + `DropdownAtom` that chooses
   * WHICH perspective the condition is about). For a host that already states the
   * perspective being edited — a drill-in titled with it, a parent list that picked it —
   * where a second identity control says the same thing twice and lets the reader
   * repoint a condition the surrounding chrome still labels with the old one. The
   * remove affordance (`onRemove`) is unaffected. Defaults to `false`, today's render.
   */
  hidePerspectiveSelect?: boolean;
  /** Props for `PerspectiveSearch` (assignee lookup) — assignee narrowing disabled if omitted. */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /**
   * Locks the perspective picker to `value.perspectiveSystemName` and hides the
   * dropdown entirely — a `focusPerspective` column host already fixed which
   * perspective this leaf conditions, so an editable dropdown that only ever
   * resolves to that one option is not real choice (mirrors
   * `RelationshipValueEditorMolecule`'s `lockRelationship`). Every existing
   * caller omits this.
   */
  lockPerspective?: boolean;
  /**
   * Starting Anyone/Specific state. Omit to derive it from `value.assignees`
   * as before (non-empty ⇒ Specific); a `focusPerspective` host always wants
   * to start Specific since assignee narrowing is the entire task.
   */
  initialMode?: 'any' | 'specific';
  className?: string;
}

const FOCUSABLE = 'input, textarea, select, button, [tabindex]:not([tabindex="-1"])';

/**
 * Invisible resolver: looks up one assignee sphere's display name so the row
 * can show a real chip label instead of a raw id, and so `PerspectiveSearch`'s
 * `initialSelectedOptions` is seeded correctly (without it, re-opening the
 * narrowing panel and adding one more assignee would silently drop every
 * previously-selected one the search hasn't re-fetched — the same shape of
 * never-widen-adjacent bug `RelatedSphereChip` exists to avoid for
 * `RelationshipValueEditorMolecule`).
 */
const AssigneeNameResolver: React.FC<{
  id: string;
  onResolved: (id: string, option: OptionType) => void;
}> = ({ id, onResolved }) => {
  const { info } = useSphereInfo(id);
  useEffect(() => {
    if (!info) return;
    onResolved(id, {
      code: id,
      name: info.properties?.name ?? id,
      glyph: info.properties?.glyph,
      glyphColor: info.properties?.glyphColor,
      imageId: info.properties?.imageId,
    });
  }, [id, info, onResolved]);
  return null;
};

const PerspectiveValueEditorMolecule: React.FC<PerspectiveValueEditorMoleculeProps> = ({
  availablePerspectives,
  value: filter,
  onChange,
  onRemove,
  readOnly = false,
  index = 0,
  hidePerspectiveSelect = false,
  perspectiveSearchProps,
  lockPerspective = false,
  initialMode,
  className = '',
}) => {
  const { t } = useTranslation();
  // The min-count input reads as the sentence around it: "at least [n] matching assignments".
  const minCountId = useId();
  const [resolvedNames, setResolvedNames] = useState<Record<string, OptionType>>({});
  const narrowRef = useRef<HTMLDivElement>(null);

  // A never-widen serialization outcome is `{kind:'perspective', filter:{}}`
  // (`contextFlowSerialization` fails closed on a malformed lane row), which
  // round-trips back as a non-nullish `{}` value — so the caller's
  // `?? DEFAULT_PERSPECTIVE_FILTER` fallback never fires and `assignees` can be
  // absent. Normalize once here rather than guarding each read.
  const assignees = useMemo(() => filter.assignees ?? [], [filter.assignees]);

  // "Anyone / Specific" is UI-only: `PerspectiveFilter` has no `mode`, and an
  // empty `assignees` list means "any assignee". Local state lets the user open
  // the Specific checklist before picking anyone; a loaded filter that already
  // has assignees starts Specific.
  const [specificMode, setSpecificMode] = useState<boolean>(
    initialMode ? initialMode === 'specific' : assignees.length > 0
  );
  const isSpecific = specificMode || assignees.length > 0;
  const showMinCount = filter.operator === 'in';

  // A `lockPerspective` host (the `focusPerspective` column) opens straight
  // into assignee narrowing — the whole point of that mode — so focus goes to
  // the search rather than sitting on the (now hidden) perspective picker.
  useEffect(() => {
    if (!lockPerspective) return;
    narrowRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    // Mount-only: a later prop change must not steal focus back.
  }, []);

  const update = useCallback(
    (partial: Partial<PerspectiveFilter>) => onChange({ ...filter, ...partial }),
    [filter, onChange]
  );

  const registerResolvedName = useCallback((id: string, option: OptionType) => {
    setResolvedNames((prev) => (prev[id]?.name === option.name ? prev : { ...prev, [id]: option }));
  }, []);

  const assigneeOptions = useMemo(
    () => assignees.map((id) => resolvedNames[id] ?? { code: id, name: id }),
    [assignees, resolvedNames]
  );

  const removeAssignee = useCallback(
    (id: string) => update({ assignees: assignees.filter((a) => a !== id) }),
    [assignees, update]
  );

  const handleAssigneeSelected = useCallback(
    (searchFilter: PerspectiveSearchFilter) => update({ assignees: searchFilter.assignees }),
    [update]
  );

  const changeAssignedTo = useCallback(
    (raw: 'any' | 'none' | 'specific' | ('any' | 'none' | 'specific')[]) => {
      if (readOnly) return;
      const mode = Array.isArray(raw) ? raw[0] : raw;
      if (mode === 'any' || mode === 'none') {
        setSpecificMode(false);
        update({
          operator: mode === 'none' ? 'not_in' : 'in',
          ...(assignees.length > 0 ? { assignees: [] } : {}),
        });
      } else {
        setSpecificMode(true);
      }
    },
    [readOnly, assignees.length, update]
  );

  // Not Specific: the two shortcuts express the whole condition — "Anyone" is
  // assigned to someone (`in`, no narrowing), "No one" is assigned to nobody
  // (`not_in`, no narrowing) — so which one reads as selected follows the
  // operator rather than a separate, orthogonal is/is-not control.
  let assignedToValue: 'specific' | 'none' | 'any' = 'any';
  if (isSpecific) assignedToValue = 'specific';
  else if (filter.operator === 'not_in') assignedToValue = 'none';

  const selectedPerspective = availablePerspectives.find(
    (p) => p.systemName === filter.perspectiveSystemName
  );

  return (
    <div
      className={`perspective-value-editor-molecule ${className}`.trim()}
      data-testid={`perspective-value-editor-${index}`}
    >
      {assignees.map((id) => (
        <AssigneeNameResolver key={id} id={id} onResolved={registerResolvedName} />
      ))}

      {/* Perspective picker — which relationship kind this condition uses. Dropped
          entirely (rather than left as an empty row) when the host states the
          perspective itself and provides no remove affordance. */}
      {(!hidePerspectiveSelect || Boolean(!readOnly && onRemove)) && (
        <div className="perspective-value-editor-molecule__perspective">
          {!hidePerspectiveSelect && (
            <>
              {selectedPerspective?.icon && (
                <span className="perspective-value-editor-molecule__perspective-glyph" aria-hidden="true">
                  <i className={selectedPerspective.icon} />
                </span>
              )}
              {lockPerspective ? (
                <span
                  className="perspective-value-editor-molecule__perspective-label"
                  data-testid={`perspective-locked-label-${index}`}
                >
                  {selectedPerspective?.label ?? filter.perspectiveSystemName}
                </span>
              ) : (
                <DropdownAtom
                  value={filter.perspectiveSystemName || null}
                  options={availablePerspectives.map((p) => ({ label: p.label, value: p.systemName }))}
                  onChange={(e) => !readOnly && update({ perspectiveSystemName: e.value ?? '' })}
                  disabled={readOnly}
                  filter
                  placeholder={t('context.editor.perspectiveFilters.perspectivePlaceholder')}
                  className="perspective-value-editor-molecule__perspective-select"
                  data-testid={`perspective-select-${index}`}
                />
              )}
            </>
          )}
          {!readOnly && onRemove && (
            <button
              type="button"
              className="perspective-value-editor-molecule__remove-btn"
              aria-label={t('context.editor.perspectiveFilters.removeFilter')}
              data-testid={`perspective-remove-${index}`}
              onClick={onRemove}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {/* Match spheres that / Assigned to — the two questions a perspective
          condition can ask, always visible (design PFAssigneeEditor). */}
      <div className="perspective-value-editor-molecule__modes">
        <div className="perspective-value-editor-molecule__field">
          <span className="perspective-value-editor-molecule__eyebrow">
            {t('context.editor.perspectiveFilters.matchSpheresThat')}
          </span>
          <SegmentedControlAtom
            options={[
              { value: 'in', label: t('context.editor.perspectiveFilters.haveIt') },
              { value: 'not_in', label: t('context.editor.perspectiveFilters.dont') },
            ]}
            value={filter.operator}
            onChange={(v) => !readOnly && update({ operator: v as 'in' | 'not_in' })}
            mode="single"
            size="small"
            ariaLabel={t('context.editor.perspectiveFilters.matchSpheresThat')}
          />
        </div>
        <div className="perspective-value-editor-molecule__field">
          <span className="perspective-value-editor-molecule__eyebrow">
            {t('context.editor.perspectiveFilters.assignedTo')}
          </span>
          <SegmentedControlAtom
            options={[
              { value: 'any', label: t('context.editor.perspectiveFilters.anyone') },
              { value: 'none', label: t('context.editor.perspectiveFilters.noOne') },
              { value: 'specific', label: t('context.editor.perspectiveFilters.specific') },
            ]}
            value={assignedToValue}
            onChange={changeAssignedTo}
            mode="single"
            size="small"
            ariaLabel={t('context.editor.perspectiveFilters.assignedTo')}
          />
        </div>
      </div>

      {/* Specific → assignee checklist + selected chips. */}
      {isSpecific && (
        <div
          ref={narrowRef}
          className="perspective-value-editor-molecule__narrow"
          data-testid={`perspective-narrow-${index}`}
        >
          {assignees.length > 0 && (
            <div className="perspective-value-editor-molecule__assignees">
              {assigneeOptions.map((opt) => (
                <ChipAtom
                  key={opt.code}
                  label={opt.name}
                  removable={!readOnly}
                  onRemove={() => {
                    removeAssignee(opt.code);
                    return false;
                  }}
                  className="perspective-value-editor-molecule__assignee-chip"
                />
              ))}
            </div>
          )}

          {perspectiveSearchProps && filter.perspectiveSystemName ? (
            <PerspectiveSearch
              perspectiveSystemName={filter.perspectiveSystemName}
              schemaSystemName={perspectiveSearchProps.schemaSystemName}
              sphereId={perspectiveSearchProps.sphereId}
              direction={perspectiveSearchProps.direction}
              hops={perspectiveSearchProps.hops}
              onAssigneeSelected={handleAssigneeSelected}
              initialSelectedOptions={assigneeOptions}
              stayOpen
              className="perspective-value-editor-molecule__search"
            />
          ) : (
            <span
              className="perspective-value-editor-molecule__narrow-hint"
              data-testid={`perspective-narrow-hint-${index}`}
            >
              {t('context.editor.perspectiveFilters.narrowNeedsContext')}
            </span>
          )}
        </div>
      )}

      {/* At least N matching assignment(s) — only meaningful for "Have it". */}
      {showMinCount && (
        <div className="perspective-value-editor-molecule__mincount">
          <span className="perspective-value-editor-molecule__eyebrow" id={`${minCountId}-lead`}>
            {t('context.editor.perspectiveFilters.atLeast')}
          </span>
          <InputNumberAtom
            value={filter.minCount ?? 1}
            onChange={(v) => update({ minCount: v ?? 1 })}
            min={1}
            type="integer"
            disabled={readOnly}
            className="perspective-value-editor-molecule__mincount-input"
            ariaLabelledBy={`${minCountId}-lead ${minCountId}-tail`}
          />
          <span className="perspective-value-editor-molecule__mincount-suffix" id={`${minCountId}-tail`}>
            {t('context.editor.perspectiveFilters.matchingAssignment', {
              count: filter.minCount ?? 1,
            })}
          </span>
        </div>
      )}
    </div>
  );
};

export default PerspectiveValueEditorMolecule;
